/** Plan and billing page (docs/specs/billing.md): manual bKash or cash payments approved by the platform. */
export const billing = {
    title: "Plan & billing",
    description: "Your plan, what it allows, and paying for it.",
    current: {
        title: "Current plan",
        trial: "Free trial until {{date}}",
        paidUntil: "Paid until {{date}} ({{cycle}})",
        noSubscription: "Not paid yet",
        pending: "Moves to {{plan}} on {{date}}",
        keep: "Keep my plan",
    },
    cycles: { monthly: "Monthly", yearly: "Yearly" },
    perCycle: { monthly: "{{price}} / month", yearly: "{{price}} / year" },
    addons: {
        title: "Add-ons",
        names: { locations: "Extra locations", devices: "Extra POS devices", sms: "SMS pack" },
        amount: {
            locations_one: "{{n}} extra location",
            locations_other: "{{n}} extra locations",
            devices_one: "{{n}} extra POS device",
            devices_other: "{{n}} extra POS devices",
            sms_one: "{{n}} SMS credit",
            sms_other: "{{n}} SMS credits",
        },
        perMonth: "{{price}} a month for {{what}}",
        perPack: "{{price}} for {{what}}",
        buy: "Buy",
        packs: "How many",
        until: "until {{date}}",
        noEnd: "never expires",
        needsPlan: "Add-ons come with a paid plan. Pay for your plan first.",
    },
    plans: {
        title: "Plans for every stage of your shop",
        choose: "Choose",
        popular: "Most popular",
        currentBadge: "Current",
        subtitle: "Every plan has the POS, one shared stock and free updates. Change plan whenever you grow.",
        saveUpTo: "Save up to {{percent}}%",
        savePercent: "save {{percent}}%",
        none: "No plans are on offer right now.",
        notOnCycle: "Not sold on this cycle",
        per: { monthly: "/ month", yearly: "/ year" },
        billedYearly: "{{price}} / month, billed yearly",
        cta: { start: "Pay for this plan", renew: "Renew", upgrade: "Upgrade", switch: "Switch to this plan" },
        everythingIn: "Everything in {{plan}}, plus:",
        includes: "Includes:",
        compare: "Compare all features",
        hideCompare: "Hide the comparison",
        feature: "Feature",
        included: "Included",
        notIncluded: "Not included",
        groups: {
            usage: "Limits",
            channels: "Ways to sell",
            stock: "Stock and operations",
            delivery: "Delivery",
            payments: "Payments",
            customers: "Customers",
            accounts_reports: "Accounts and reports",
            team: "Team",
            integrations: "Integrations",
            support: "Support",
        },
        unlimited: "Unlimited",
        limits: {
            "locations.max": "{{limit}} locations",
            "users.max": "{{limit}} staff users",
            "devices.max": "{{limit}} POS devices",
            "products.max": "{{limit}} products",
            "orders.monthly.max": "{{limit}} orders a month",
            "storefront.landing_pages.max": "{{limit}} landing pages",
        },
    },
    quote: {
        title: "{{plan}}, {{cycle}}",
        kinds: {
            new: "Your first paid period",
            renewal: "Renewal for the next period",
            upgrade: "Upgrade now: only the rest of this period is charged",
            downgrade: "No payment: the smaller plan starts when this period ends",
        },
        subtotal: "Price",
        discount: "Discount",
        vat: "VAT",
        total: "To pay",
        period: "Period",
        coupon: "Coupon code",
        applyCoupon: "Apply",
        scheduleDowngrade: "Move at period end",
        back: "Back to plans",
    },
    pay: {
        method: "How will you pay?",
        bkash: "bKash",
        cash: "Cash",
        bkashSteps: "Open your bKash app → Send Money to {{number}} ({{type}}) → amount {{amount}}. Then enter your bKash number and the TrxID below.",
        accountTypes: { personal: "personal", agent: "agent", merchant: "merchant" },
        senderPhone: "Your bKash number",
        trxId: "Transaction ID (TrxID)",
        cashHelp: "Pay in cash to Sobthik. Your plan starts once the payment is confirmed.",
        note: "Note (optional)",
        submit: "Send for approval",
        copy: "Copy",
        copied: "Copied",
    },
    open: {
        waitingTitle: "Payment waiting for approval",
        waiting: "We are checking your payment of {{amount}}. Your plan starts as soon as it is approved.",
        withdraw: "Withdraw",
        rejectedTitle: "Payment not approved",
        rejected: "Reason: {{reason}}. Please check and send again.",
        openTitle: "Invoice {{number}} is not paid yet",
    },
    invoices: {
        title: "Invoices",
        empty: "No invoices yet.",
        number: "Number",
        issued: "Issued",
        total: "Total",
        status: "Status",
        pdf: "PDF",
        statuses: {
            open: "Open",
            awaiting_approval: "Waiting for approval",
            paid: "Paid",
            overdue: "Overdue",
            credited: "Cancelled",
        },
    },
    account: {
        grace: "Your payment is overdue. Everything works until {{date}}; please pay soon.",
        restricted: "Payment overdue: adding products, staff and locations is paused. POS and orders keep working. Suspension on {{date}}.",
        suspendedTitle: "Your account is suspended",
        suspendedBody: "Your POS still sells and syncs. Pay for your plan to use everything again — it is restored at once.",
        payNow: "Pay now",
    },
    submitted: "Sent. We will tell you as soon as it is approved.",
    downgradeScheduled: "Your plan will change when this period ends.",
    licence: {
        title: "Licence",
        description: "This is a dedicated Sobthik instance. Its limits come from your licence.",
        customer: "Licensed to",
        expires: "Valid until",
        issued: "Issued",
        id: "Licence ID",
        limits: "Limits and features",
        unlimited: "Unlimited",
        included: "Included",
        notIncluded: "Not included",
        valid: "Valid",
        invalid: "Not valid",
        invalidBody: "The licence could not be verified: {{problem}}. Contact your Sobthik account manager; the POS keeps selling.",
        expiresSoon: "The licence ends on {{date}}. Contact your Sobthik account manager to renew it.",
        expired: "The licence ended on {{date}}. Some actions will be limited step by step; the POS keeps selling.",
        features: {
            "feature.pos": "POS",
            "feature.multi_location": "Several locations",
            "feature.custom_roles": "Custom roles",
            "feature.api": "API access",
            "feature.storefront": "Online store",
            "feature.custom_domain": "Own shop domain",
        },
        extraLimits: {
            "sms.credits": "{{limit}} SMS credits",
        },
    },
};
//# sourceMappingURL=billing.js.map